import { TriangleAlert } from "lucide-react";
import { useLocation } from "react-router";
import { Link } from "@/components/ui/link";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./collection-tabs.i18n";

type CollectionTabsProps = {
  collectionId: string;
  hasRiskModel: boolean;
  hasSustainability: boolean;
  hasDynamicPortfolios: boolean;
  hasWrapperTypes: boolean;
  /** QL-383 — the collection's "Use product types" setting gates the Product Types tab. */
  hasProductTypes: boolean;
  /** QL-375 — the collection's "Enable mandates" setting gates the Mandates tab. */
  hasMandates: boolean;
  /** Tabs flagged true render a warning icon; the explanation lives inside the tab's page. */
  tabWarnings?: Partial<Record<TabKey, boolean>>;
};

type TabKey =
  | "products"
  | "productTypes"
  | "goalTypes"
  | "wrapperTypes"
  | "clientPreferences"
  | "platforms"
  | "mandates"
  | "sustainability"
  | "documents"
  | "assetClasses"
  | "correlations"
  | "portfolios"
  | "dynamicPortfolios"
  | "costs"
  | "settings";

const allTabs: {
  key: TabKey;
  href: string;
  requiresRiskModel: boolean;
  requiresSustainability?: boolean;
  requiresDynamicPortfolios?: boolean;
  requiresWrapperTypes?: boolean;
  requiresProductTypes?: boolean;
  requiresMandates?: boolean;
}[] = [
  { key: "products", href: "products", requiresRiskModel: false },
  {
    key: "productTypes",
    href: "product-types",
    requiresRiskModel: false,
    requiresProductTypes: true,
  },
  { key: "goalTypes", href: "goal-types", requiresRiskModel: false },
  {
    key: "wrapperTypes",
    href: "wrapper-types",
    requiresRiskModel: false,
    requiresWrapperTypes: true,
  },
  { key: "clientPreferences", href: "client-preferences", requiresRiskModel: false },
  { key: "platforms", href: "platforms", requiresRiskModel: false },
  // QL-375 — templates are resolved by the risk model's scores, so the tab
  // needs one; the rules card has no such dependency but shares the tab.
  { key: "mandates", href: "mandates", requiresRiskModel: true, requiresMandates: true },
  {
    key: "sustainability",
    href: "sustainability",
    requiresRiskModel: false,
    requiresSustainability: true,
  },
  { key: "documents", href: "product-documents", requiresRiskModel: false },
  { key: "assetClasses", href: "asset-classes", requiresRiskModel: false },
  { key: "correlations", href: "correlations", requiresRiskModel: false },
  { key: "portfolios", href: "portfolios-list", requiresRiskModel: true },
  {
    key: "dynamicPortfolios",
    href: "dynamic-portfolios",
    requiresRiskModel: true,
    requiresDynamicPortfolios: true,
  },
  { key: "costs", href: "costs", requiresRiskModel: false },
  { key: "settings", href: "settings", requiresRiskModel: false },
];

export function CollectionTabs({
  collectionId,
  hasRiskModel,
  hasSustainability,
  hasDynamicPortfolios,
  hasWrapperTypes,
  hasProductTypes,
  hasMandates,
  tabWarnings,
}: CollectionTabsProps) {
  const { pathname } = useLocation();
  const t = useT(T);

  const basePath = `/settings/portfolios/${collectionId}`;

  const tabs = allTabs.filter(
    (tab) =>
      (!tab.requiresRiskModel || hasRiskModel) &&
      (!tab.requiresSustainability || hasSustainability) &&
      (!tab.requiresDynamicPortfolios || hasDynamicPortfolios) &&
      (!tab.requiresWrapperTypes || hasWrapperTypes) &&
      (!tab.requiresProductTypes || hasProductTypes) &&
      (!tab.requiresMandates || hasMandates),
  );

  return (
    <nav className="inline-flex h-auto min-h-10 max-w-full flex-wrap items-center justify-start gap-0.5 rounded-lg bg-muted p-1 text-muted-foreground">
      {tabs.map((tab) => {
        const href = `${basePath}/${tab.href}`;
        // Also highlight Portfolios tab when viewing a portfolio detail page
        const isActive =
          pathname === href ||
          pathname.startsWith(`${href}/`) ||
          (tab.href === "portfolios-list" && pathname.startsWith(`${basePath}/portfolio/`));

        return (
          <Link
            key={tab.href}
            to={href}
            className={cn(
              "inline-flex h-8 items-center justify-center whitespace-nowrap rounded-md px-3 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              isActive
                ? "bg-card text-foreground shadow"
                : "hover:bg-card/50 hover:text-foreground",
            )}
          >
            {t[tab.key]}
            {tabWarnings?.[tab.key] && (
              <>
                <TriangleAlert aria-hidden="true" className="ml-1.5 h-3.5 w-3.5 text-warning" />
                <span className="sr-only">{t.tabWarning}</span>
              </>
            )}
          </Link>
        );
      })}
    </nav>
  );
}
